import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { LEGAL_CONTENT } from "@/lib/legal";

export default function Legal() {
  const { lang, t } = useApp();
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const c = LEGAL_CONTENT[lang] || LEGAL_CONTENT.fr;
  const ui = c.ui;
  const canManageDocs = isRole("president", "tresorier", "conseil");
  const isPresident = isRole("president");

  const [contacts, setContacts] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showReg, setShowReg] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => { api.get("/legal/contacts").then((r) => setContacts(r.data || {})).catch(() => setContacts({})); }, []);

  const publishReglement = async () => {
    setPublishing(true);
    try {
      await api.post("/documents", {
        category: "reglement", title: c.reglementTemplate.title,
        description: ui.reglement_doc_sub, body: c.reglementTemplate.body, visibility: "public",
      });
      toast.success(ui.published_ok);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setPublishing(false); }
  };

  return (
    <div className="space-y-4" data-testid="legal-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0" data-testid="legal-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-tight">{ui.title}</h1>
          <p className="text-sm text-muted-foreground">{ui.sub}</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex gap-3" data-testid="legal-disclaimer">
        <Icons.TriangleAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-amber-700 dark:text-amber-300">{ui.disclaimer_title}</p>
          <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{c.disclaimer}</p>
        </div>
      </div>

      {/* Intro */}
      <Section icon="Scale" title={ui.intro_title} testid="legal-intro">
        <div className="space-y-2">
          {c.intro.map((p, i) => <p key={p.slice(0, 40)} className="text-sm leading-relaxed text-foreground/90">{p}</p>)}
        </div>
      </Section>

      {/* Laws */}
      <Section icon="BookText" title={ui.laws_title} testid="legal-laws">
        <div className="space-y-3">
          {c.laws.map((law, i) => (
            <div key={law.code} className="rounded-xl bg-secondary/50 p-3" data-testid={`legal-law-${i}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="text-xs font-bold bg-brand text-white px-2 py-0.5 rounded-full">{law.code}</span>
                <span className="text-xs font-semibold text-muted-foreground">{law.year}</span>
                <span className="font-bold text-sm">{law.title}</span>
              </div>
              <ul className="space-y-1.5">
                {law.points.map((pt, j) => (
                  <li key={pt.slice(0, 40)} className="text-sm flex gap-2 text-foreground/90">
                    <Icons.ChevronsRight className="h-4 w-4 text-brand dark:text-primary shrink-0 mt-0.5 rtl:rotate-180" /> {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Syndic obligations */}
      <Section icon="UserCog" title={ui.syndic_title} testid="legal-syndic">
        <BulletList items={c.syndic} icon="Check" />
      </Section>

      {/* AG rules */}
      <Section icon="Gavel" title={ui.ag_title} testid="legal-ag">
        <BulletList items={c.ag} icon="Check" />
      </Section>

      {/* Majorities */}
      <Section icon="Vote" title={ui.majorities_title} testid="legal-majorities">
        <div className="space-y-2">
          {c.majorities.map((m, i) => (
            <div key={m.level} className="rounded-xl bg-secondary/50 p-3" data-testid={`legal-maj-${i}`}>
              <p className="font-bold text-sm text-brand dark:text-primary">{m.level}</p>
              <p className="text-sm text-foreground/90 mt-0.5">{m.desc}</p>
            </div>
          ))}
          <div className="rounded-xl bg-majorelle/10 p-3 text-sm text-foreground/90 flex gap-2">
            <Icons.Info className="h-4 w-4 text-majorelle shrink-0 mt-0.5" /> {c.quorum_note}
          </div>
        </div>
      </Section>

      {/* Conseil syndical */}
      <Section icon="Users" title={ui.conseil_title} testid="legal-conseil">
        <p className="text-sm leading-relaxed text-foreground/90">{c.conseil}</p>
      </Section>

      {/* Règlement obligatoire */}
      <Section icon="ScrollText" title={ui.reglement_title} testid="legal-reglement">
        <BulletList items={c.reglement} icon="Dot" />
      </Section>

      {/* Conciliation */}
      <Section icon="Handshake" title={ui.conciliation_title} testid="legal-conciliation">
        <BulletList items={c.conciliation} icon="Check" />
      </Section>

      {/* Checklist */}
      <Section icon="ListChecks" title={ui.checklist_title} testid="legal-checklist">
        <ul className="space-y-2">
          {c.checklist.map((item, i) => (
            <li key={item.slice(0, 40)} className="text-sm flex gap-2 items-start" data-testid={`legal-check-${i}`}>
              <Icons.CircleCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" /> {item}
            </li>
          ))}
        </ul>
      </Section>

      {/* Règlement template */}
      <Section icon="FileText" title={ui.reglement_doc_title} testid="legal-reglement-doc">
        <p className="text-sm text-muted-foreground mb-3">{ui.reglement_doc_sub}</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowReg((v) => !v)} data-testid="legal-toggle-reglement"
            className="h-10 px-4 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold flex items-center gap-1.5">
            <Icons.Eye className="h-4 w-4" /> {showReg ? ui.hide_reglement : ui.show_reglement}
          </button>
          {canManageDocs && (
            <button onClick={publishReglement} disabled={publishing} data-testid="legal-publish-reglement"
              className="h-10 px-4 rounded-xl bg-brand text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">
              <Icons.Upload className="h-4 w-4" /> {ui.publish_btn}
            </button>
          )}
        </div>
        {showReg && (
          <pre className="mt-3 text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed rounded-xl bg-secondary/50 p-3 max-h-96 overflow-y-auto font-sans" data-testid="legal-reglement-text">
            {c.reglementTemplate.body}
          </pre>
        )}
      </Section>

      {/* Legal contacts */}
      <Section icon="Phone" title={ui.contacts_title} testid="legal-contacts"
        action={isPresident && (
          <button onClick={() => setEditing(true)} data-testid="legal-edit-contacts"
            className="h-9 px-3 rounded-full bg-majorelle text-white text-sm font-semibold flex items-center gap-1">
            <Icons.Pencil className="h-4 w-4" /> {ui.edit_contacts}
          </button>
        )}>
        <p className="text-xs text-muted-foreground mb-2">{ui.contacts_sub}</p>
        <ContactsView contacts={contacts || {}} ui={ui} />
      </Section>

      {editing && (
        <ContactsModal contacts={contacts || {}} ui={ui}
          onClose={() => setEditing(false)}
          onSaved={(data) => { setContacts(data); setEditing(false); }} />
      )}
    </div>
  );
}

function Section({ icon, title, children, testid, action }) {
  const Ico = Icons[icon] || Icons.Scale;
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-9 w-9 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
          <Ico className="h-5 w-5 text-brand dark:text-primary" />
        </div>
        <h2 className="font-bold text-base flex-1 min-w-0">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function BulletList({ items, icon }) {
  const Ico = Icons[icon] || Icons.Check;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={it.slice(0, 40)} className="text-sm flex gap-2 items-start text-foreground/90">
          <Ico className="h-4 w-4 text-brand dark:text-primary shrink-0 mt-0.5" /> {it}
        </li>
      ))}
    </ul>
  );
}

const CONTACT_GROUPS = [
  { key: "tribunal", label: "tribunal", hasAddress: true },
  { key: "adoul", label: "adoul", hasAddress: false },
  { key: "notaire", label: "notaire", hasAddress: false },
  { key: "avocat", label: "avocat", hasAddress: false },
];

function ContactsView({ contacts, ui }) {
  const filled = CONTACT_GROUPS.filter((g) => contacts[`${g.key}_name`] || contacts[`${g.key}_phone`]);
  if (filled.length === 0 && !contacts.note) {
    return <p className="text-sm text-muted-foreground" data-testid="legal-contacts-empty">{ui.empty_contacts}</p>;
  }
  return (
    <div className="space-y-2">
      {filled.map((g) => (
        <div key={g.key} className="rounded-xl bg-secondary/50 p-3" data-testid={`legal-contact-${g.key}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{ui[g.label]}</p>
          {contacts[`${g.key}_name`] && <p className="font-semibold text-sm">{contacts[`${g.key}_name`]}</p>}
          {contacts[`${g.key}_phone`] && (
            <a href={`tel:${contacts[`${g.key}_phone`]}`} className="text-sm text-brand dark:text-primary flex items-center gap-1" data-testid={`legal-contact-phone-${g.key}`}>
              <Icons.Phone className="h-3.5 w-3.5" /> {contacts[`${g.key}_phone`]}
            </a>
          )}
          {g.hasAddress && contacts[`${g.key}_address`] && <p className="text-xs text-muted-foreground mt-0.5">{contacts[`${g.key}_address`]}</p>}
        </div>
      ))}
      {contacts.note && <p className="text-sm text-foreground/90 italic">{contacts.note}</p>}
    </div>
  );
}

function ContactsModal({ contacts, ui, onClose, onSaved }) {
  const [form, setForm] = useState({
    tribunal_name: contacts.tribunal_name || "", tribunal_phone: contacts.tribunal_phone || "", tribunal_address: contacts.tribunal_address || "",
    adoul_name: contacts.adoul_name || "", adoul_phone: contacts.adoul_phone || "",
    notaire_name: contacts.notaire_name || "", notaire_phone: contacts.notaire_phone || "",
    avocat_name: contacts.avocat_name || "", avocat_phone: contacts.avocat_phone || "",
    note: contacts.note || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full h-11 rounded-xl border border-input bg-background px-3 outline-none text-sm";

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/legal/contacts", form); toast.success(ui.save_contacts); onSaved(data); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="legal-contacts-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-card border border-border rounded-t-2xl sm:rounded-2xl p-4 pb-0 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg">{ui.contacts_title}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        {CONTACT_GROUPS.map((g) => (
          <div key={g.key} className="space-y-2">
            <p className="text-sm font-bold text-muted-foreground">{ui[g.label]}</p>
            <input value={form[`${g.key}_name`]} onChange={(e) => set(`${g.key}_name`, e.target.value)} placeholder={ui.name_ph} className={input} data-testid={`legal-in-${g.key}-name`} />
            <input value={form[`${g.key}_phone`]} onChange={(e) => set(`${g.key}_phone`, e.target.value)} placeholder={ui.phone_ph} className={input} data-testid={`legal-in-${g.key}-phone`} />
            {g.hasAddress && <input value={form[`${g.key}_address`]} onChange={(e) => set(`${g.key}_address`, e.target.value)} placeholder={ui.address_ph} className={input} data-testid={`legal-in-${g.key}-address`} />}
          </div>
        ))}
        <textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder={ui.note_ph} rows={2}
          className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none text-sm" data-testid="legal-in-note" />
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-card border-t border-border flex gap-2">
          <button onClick={save} disabled={busy} data-testid="legal-save-contacts" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{ui.save_contacts}</button>
        </div>
      </div>
    </div>
  );
}
